import React, { useState, useCallback } from 'react';
import {
  GlassControlProps,
  CarWindowType,
  CarWindowDto,
} from './GlassControl.types';
import { WindowLabel } from './WindowLabel';
import { WindowOverlay } from './WindowOverlay';
import { GlassControlMobile } from './GlassControlMobile';
import SectionSubtitle from '../Typography/SectionSubtitle';

// Importy zdjęć samochodu
import carTopView from './car.png';
import carLeftView from './car-left.png';
import carRightView from './car-right.png';
import { SectionSummary } from '../SectionSummary';

interface OverlayConfig {
  type: CarWindowType;
  position: { top: string; left: string; width: string; height: string };
}

/**
 * Komponent GlassControl - kontrola szyb w pojeździe
 * Wyświetla trzy widoki samochodu (góra, lewa, prawa strona) z informacjami o szybach
 */
export function GlassControl({ data, className = '' }: GlassControlProps) {
  const windows = data?.fields || ({} as Record<CarWindowType, CarWindowDto>);
  const expertSummary = data?.expertSummary;

  const [hoveredWindow, setHoveredWindow] = useState<CarWindowType | null>(null);
  const handleMouseEnter = useCallback((type: CarWindowType) => setHoveredWindow(type), []);
  const handleMouseLeave = useCallback(() => setHoveredWindow(null), []);

  // Sprawdź czy są jakiekolwiek dane
  const hasData = Object.keys(windows).length > 0;

  if (!hasData) {
    return (
      <div className={className}>
        <SectionSubtitle
          title="Kontrola szyb"
          description="Weryfikacja szyb – sprawdzenie oryginalności i stanu szyb."
        />
        <div className="mt-11 p-8 bg-gray-50 rounded-[15px] text-center text-gray-500">
          Brak danych o szybach w pojeździe
        </div>
      </div>
    );
  }

  // Definicje pozycji etykiet dla różnych widoków
  // Pozycje w procentach względem kontenera obrazu
  const topViewWindows: Array<{
    type: CarWindowType;
    label: string;
    position: { top: string; left: string };
    showPointer?: boolean;
    pointerDirection?: 'top' | 'bottom' | 'left' | 'right' | 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
  }> = [
      {
        type: 'FRONT',
        label: 'Przednia',
        position: { top: '18%', left: '50%' },
        showPointer: false,
        pointerDirection: 'top',
      },
      {
        type: 'REAR',
        label: 'Tylna',
        position: { top: '75%', left: '50%' },
        showPointer: false,
        pointerDirection: 'bottom',
      },
    ];

  const leftViewWindows: Array<{
    type: CarWindowType;
    label: string;
    position: { top: string; left: string };
    showPointer?: boolean;
    pointerDirection?: 'top' | 'bottom' | 'left' | 'right' | 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
  }> = [
      {
        type: 'LEFT_FRONT_DOOR',
        label: 'Lewe przednie drzwi',
        position: { top: '45%', left: '50%' },
        showPointer: true,
        pointerDirection: 'top-right',
      },
      {
        type: 'LEFT_REAR_DOOR',
        label: 'Lewe tylne drzwi',
        position: { top: '0%', left: '60%' },
        showPointer: true,
        pointerDirection: 'bottom-left',
      },
      {
        type: 'LEFT_TRIANGLE',
        label: 'Lewy tył trójkąt',
        position: { top: '42%', left: '68%' },
        showPointer: true,
        pointerDirection: 'top-left',
      },
    ];

  const rightViewWindows: Array<{
    type: CarWindowType;
    label: string;
    position: { top: string; left: string };
    showPointer?: boolean;
    pointerDirection?: 'top' | 'bottom' | 'left' | 'right' | 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
  }> = [
      {
        type: 'RIGHT_REAR_DOOR',
        label: 'Prawe tylne drzwi',
        position: { top: '0%', left: '40%' },
        pointerDirection: 'bottom-right',
        showPointer: true,
      },
      {
        type: 'RIGHT_FRONT_DOOR',
        label: 'Prawe przednie drzwi',
        position: { top: '1%', left: '45%' },
        showPointer: true,
        pointerDirection: 'bottom-left',
      },
      {
        type: 'RIGHT_TRIANGLE',
        label: 'Prawy tył trójkąt',
        position: { top: '42%', left: '18%' },
        showPointer: true,
        pointerDirection: 'top-left',
      },
    ];

  // Konfiguracja pozycji nakładek SVG - widok z góry
  const topViewOverlays: OverlayConfig[] = [
    {
      type: 'FRONT',
      position: { top: '23%', left: '29%', width: '43%', height: '20%' },
    },
    {
      type: 'REAR',
      position: { top: '69.5%', left: '34.5%', width: '31%', height: '16%' },
    },
  ];

  // Konfiguracja pozycji nakładek SVG - strona prawa
  const rightViewOverlays: OverlayConfig[] = [
    {
      type: 'RIGHT_REAR_DOOR',
      position: { top: '14.2%', left: '25%', width: '18%', height: '32%' },
    },
    {
      type: 'RIGHT_FRONT_DOOR',
      position: { top: '14.8%', left: '42.2%', width: '21.1%', height: '32%' },
    },
    {
      type: 'RIGHT_TRIANGLE',
      position: { top: '17.1%', left: '18.7%', width: '9.4%', height: '26%' },
    },
  ];

  // Konfiguracja pozycji nakładek SVG - strona lewa
  const leftViewOverlays: OverlayConfig[] = [
    {
      type: 'LEFT_FRONT_DOOR',
      position: { top: '14.8%', left: '35.3%', width: '21.1%', height: '32%' },
    },
    {
      type: 'LEFT_REAR_DOOR',
      position: { top: '14.4%', left: '55.8%', width: '18%', height: '32%' },
    },
    {
      type: 'LEFT_TRIANGLE',
      position: { top: '17.5%', left: '70.7%', width: '9.4%', height: '26%' },
    },
  ];

  // Funkcja pomocnicza do określania wariantu nakładki
  const getOverlayVariant = (windowData: CarWindowDto): 'normal' | 'replaced' | 'absence' => {
    if (windowData.absence) return 'absence';
    return 'normal';
  };

  // Funkcja renderująca nakładkę SVG
  const renderOverlay = (config: OverlayConfig) => {
    const windowData = windows[config.type];
    if (!windowData) return null;

    return (
      <div
        key={`overlay-${config.type}`}
        className="absolute"
        style={{
          top: config.position.top,
          left: config.position.left,
          width: config.position.width,
          height: config.position.height,
        }}
      >
        <WindowOverlay
          type={config.type}
          variant={getOverlayVariant(windowData)}
          style={{ width: '100%', height: '100%' }}
          isHighlighted={hoveredWindow === config.type}
          onMouseEnter={() => handleMouseEnter(config.type)}
          onMouseLeave={handleMouseLeave}
        />
      </div>
    );
  };

  return (
    <div className={className}>
      {/* Nagłówek sekcji */}
      <SectionSubtitle
        title="Kontrola szyb"
        description="Weryfikacja szyb – sprawdzenie oryginalności i stanu szyb."
      />

      {/* Wersja mobilna - poniżej lg */}
      <div className="mt-6 lg:hidden">
        <GlassControlMobile data={data} />
      </div>

      {/* Główna ramka z widokami - wersja desktop od lg w górę */}
      <div className="hidden lg:grid mt-11 border border-gray-200 rounded-[15px] overflow-hidden bg-white grid-cols-1 xl:grid-cols-[3fr_6fr] divide-x divide-gray-200 rounded-b-none border-b-0">
        {/* Widok z góry / tył */}
        <div className="relative lg:p-2 xl:p-4 2xl:p-8">
          <h3 className="text-[12px] font-normal text-text-primary mb-4">
            Tył / Przód
          </h3>
          <div className="flex relative justify-center w-full">
            {/* Zdjęcie samochodu z góry */}
            <div className="relative h-[600px] w-[400px] flex-shrink-0 flex items-center justify-center">
              <img
                src={carTopView}
                alt="Widok samochodu z góry"
                className="object-contain w-full h-full"
              />

              {/* Nakładki SVG na szyby */}
              {topViewOverlays.map(renderOverlay)}

              {/* Etykiety szyb dla widoku z góry */}
              {topViewWindows.map(
                ({ type, label, position, showPointer, pointerDirection }) =>
                  windows[type] ? (
                    <WindowLabel
                      key={type}
                      data={windows[type]}
                      label={label}
                      position={position}
                      isActive={type === 'FRONT'}
                      showPointer={showPointer}
                      pointerDirection={pointerDirection}
                      isHighlighted={hoveredWindow === type}
                      onMouseEnter={() => handleMouseEnter(type)}
                      onMouseLeave={handleMouseLeave}
                    />
                  ) : null
              )}
            </div>
          </div>
        </div>

        {/* Widoki boczne */}
        <div className="grid w-full divide-y divide-gray-200">

          {/* Strona prawa */}
          <div className="relative lg:p-2 xl:p-4 2xl:p-8">
            <h3 className="text-[12px] font-normal text-text-primary mb-4">
              Strona prawa
            </h3>
            <div className="flex relative justify-center">
              {/* Zdjęcie samochodu - prawa strona */}
              <div className="relative h-[300px] w-[500px] flex-shrink-0 flex items-center justify-center">
                <img
                  src={carRightView}
                  alt="Widok samochodu - prawa strona"
                  className="object-contain w-full h-full"
                />

                {/* Nakładki SVG na szyby */}
                {rightViewOverlays.map(renderOverlay)}

                {/* Etykiety szyb dla prawej strony */}
                {rightViewWindows.map(
                  ({ type, label, position, showPointer, pointerDirection }) =>
                    windows[type] ? (
                      <WindowLabel
                        key={type}
                        data={windows[type]}
                        label={label}
                        position={position}
                        showPointer={showPointer}
                        pointerDirection={pointerDirection}
                        isHighlighted={hoveredWindow === type}
                        onMouseEnter={() => handleMouseEnter(type)}
                        onMouseLeave={handleMouseLeave}
                      />
                    ) : null
                )}
              </div>
            </div>
          </div>

          {/* Strona lewa */}
          <div className="relative lg:p-2 xl:p-4 2xl:p-8">
            <h3 className="text-[12px] font-normal text-text-primary mb-4">
              Strona lewa
            </h3>
            <div className="flex relative justify-center">
              {/* Zdjęcie samochodu - lewa strona */}
              <div className="relative h-[300px] w-[500px] flex-shrink-0 flex items-center justify-center">
                <img
                  src={carLeftView}
                  alt="Widok samochodu - lewa strona"
                  className="object-contain w-full h-full"
                />

                {/* Nakładki SVG na szyby */}
                {leftViewOverlays.map(renderOverlay)}

                {/* Etykiety szyb dla lewej strony */}
                {leftViewWindows.map(
                  ({ type, label, position, showPointer, pointerDirection }) =>
                    windows[type] ? (
                      <WindowLabel
                        key={type}
                        data={windows[type]}
                        label={label}
                        position={position}
                        showPointer={showPointer}
                        pointerDirection={pointerDirection}
                        isHighlighted={hoveredWindow === type}
                        onMouseEnter={() => handleMouseEnter(type)}
                        onMouseLeave={handleMouseLeave}
                      />
                    ) : null
                )}
              </div>
            </div>
          </div>


        </div>
      </div>

      {/* Podsumowanie eksperta - wersja desktop */}
      {expertSummary && (
        <div className="hidden lg:block">
          <SectionSummary iconName="car-hood" title="Podsumowanie Rzeczoznawcy dotyczące kontroli szyb" content={expertSummary || ''} className='rounded-t-none' />
        </div>
      )}
    </div>
  );
}

export default GlassControl;
